import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Poll } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { pollsService } from '../../services/polls.service';
import { canEndPolls, canManagePoll } from '../../utils/roles';
import './PollFeedCard.css';

interface PollFeedCardProps {
  poll: Poll;
  onUpdated: (poll: Poll) => void;
  onDeleted?: (pollId: string) => void;
}

const PollFeedCard: React.FC<PollFeedCardProps> = ({ poll, onUpdated, onDeleted }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selected, setSelected] = useState<number | null>(poll.myVote ?? null);
  const [voteBusy, setVoteBusy] = useState(false);
  const [endBusy, setEndBusy] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [showEndConfirm, setShowEndConfirm] = useState(false);

  useEffect(() => {
    setSelected(poll.myVote ?? null);
  }, [poll.id, poll.myVote]);

  const canEndBtn = canEndPolls(user?.role);
  const canManage = canManagePoll(poll, user?.id, user?.role);

  const handleEditInPollsPage = () => {
    navigate('/polls', { state: { editPollId: poll.id } });
  };

  const handleDeletePoll = () => {
    setShowDeleteConfirm(true);
  };

  const cancelDelete = useCallback(() => {
    if (deleteBusy) return;
    setShowDeleteConfirm(false);
  }, [deleteBusy]);

  const cancelEnd = useCallback(() => {
    if (endBusy) return;
    setShowEndConfirm(false);
  }, [endBusy]);

  useEffect(() => {
    if (!showDeleteConfirm && !showEndConfirm) return;
    const onEsc = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (showEndConfirm) {
        cancelEnd();
        return;
      }
      cancelDelete();
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [showDeleteConfirm, showEndConfirm, cancelDelete, cancelEnd]);

  const confirmDelete = async () => {
    if (deleteBusy) return;
    setDeleteBusy(true);
    try {
      await pollsService.remove(poll.id);
      setShowDeleteConfirm(false);
      onDeleted?.(poll.id);
    } catch (err: any) {
      const msg = err?.response?.data?.message;
      alert(Array.isArray(msg) ? msg.join(' ') : msg || 'Could not delete poll');
    } finally {
      setDeleteBusy(false);
    }
  };

  const handleVote = async () => {
    if (selected === null || poll.isEnded) return;
    setVoteBusy(true);
    try {
      const updated = await pollsService.vote(poll.id, selected);
      onUpdated(updated);
    } catch (err: any) {
      const msg = err?.response?.data?.message;
      alert(Array.isArray(msg) ? msg.join(' ') : msg || 'Vote failed');
    } finally {
      setVoteBusy(false);
    }
  };

  const handleEnd = () => {
    setShowEndConfirm(true);
  };

  const confirmEndPoll = async () => {
    if (endBusy) return;
    setEndBusy(true);
    try {
      const updated = await pollsService.endPoll(poll.id);
      setShowEndConfirm(false);
      onUpdated(updated);
    } catch (err: any) {
      const msg = err?.response?.data?.message;
      alert(Array.isArray(msg) ? msg.join(' ') : msg || 'Could not end poll');
    } finally {
      setEndBusy(false);
    }
  };

  const scopeLabel =
    poll.accessScope === 'COMPANY'
      ? 'Company'
      : poll.accessScope === 'BRANCH'
        ? `Branch · ${poll.branch || '—'}`
        : poll.branch
          ? `Dept · ${poll.department || '—'} · ${poll.branch}`
          : `Dept · ${poll.department || '—'} · All branches`;

  return (
    <>
      <div className="post-card poll-feed-card" data-poll-id={poll.id}>
        <div className="poll-feed-accent" aria-hidden />

      <div className="poll-feed-top">
        <div className="poll-feed-brand">
          <span className="poll-feed-icon" aria-hidden>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M4 12h4l2-7 4 14 2-7h4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className="poll-feed-eyebrow">Poll</span>
        </div>
        <span
          className={`poll-feed-scope-pill poll-feed-scope--${(poll.accessScope || 'COMPANY').toLowerCase()}`}
        >
          {scopeLabel}
        </span>
      </div>

      {canManage && (
        <div className="poll-feed-manage">
          {!poll.isEnded && (
            <button type="button" className="poll-feed-manage-btn" onClick={handleEditInPollsPage}>
              Edit
            </button>
          )}
          <button
            type="button"
            className="poll-feed-manage-btn poll-feed-manage-btn--danger"
            onClick={handleDeletePoll}
          >
            Delete
          </button>
        </div>
      )}

      <div className="poll-feed-body">
        <h3 className="poll-feed-title">{poll.title}</h3>
        {poll.description && <p className="poll-feed-desc">{poll.description}</p>}
      </div>

      {poll.results && poll.isEnded && (
        <div className="poll-feed-results" role="region" aria-label="Poll results">
          <div className="poll-feed-results-head">
            <span className="poll-feed-results-title">Results</span>
            <span className="poll-feed-results-total">{poll.results.total} votes</span>
          </div>
          <ul className="poll-feed-result-list">
            {poll.choices.map((label, i) => {
              const count = poll.results!.counts[i] ?? 0;
              const pct = poll.results!.total
                ? Math.round((count / poll.results!.total) * 1000) / 10
                : 0;
              return (
                <li key={i} className="poll-feed-result-item">
                  <div className="poll-feed-result-row">
                    <span className="poll-feed-result-label">{label}</span>
                    <span className="poll-feed-result-stats">
                      <strong>{pct}%</strong>
                      <span className="poll-feed-result-count">{count}</span>
                    </span>
                  </div>
                  <div className="poll-feed-bar" aria-hidden>
                    <div className="poll-feed-bar-fill" style={{ width: `${Math.max(pct, 2)}%` }} />
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {!poll.isEnded && poll.myVote === null && (
        <div className="poll-feed-vote">
          <p className="poll-feed-vote-hint">Select one option</p>
          <div className="poll-feed-options" role="radiogroup" aria-label="Poll choices">
            {poll.choices.map((label, i) => (
              <label
                key={i}
                className={`poll-feed-option${selected === i ? ' poll-feed-option--selected' : ''}`}
              >
                <input
                  type="radio"
                  name={`feed-poll-${poll.id}`}
                  checked={selected === i}
                  onChange={() => setSelected(i)}
                />
                <span className="poll-feed-option-index">{i + 1}</span>
                <span className="poll-feed-option-text">{label}</span>
              </label>
            ))}
          </div>
          <button
            type="button"
            className="poll-feed-submit"
            disabled={selected === null || voteBusy}
            onClick={handleVote}
          >
            {voteBusy ? 'Submitting…' : 'Submit vote'}
          </button>
        </div>
      )}

      {!poll.isEnded && poll.myVote !== null && (
        <div className="poll-feed-await">
          <span className="poll-feed-await-icon" aria-hidden>✓</span>
          <div>
            <p className="poll-feed-await-title">You voted</p>
            <p className="poll-feed-await-body">
              <strong>{poll.choices[poll.myVote]}</strong>
              <span className="poll-feed-await-sub"> Results appear when an admin ends the poll.</span>
            </p>
          </div>
        </div>
      )}

        {canEndBtn && !poll.isEnded && (
          <button
            type="button"
            className="poll-feed-end"
            disabled={endBusy}
            onClick={handleEnd}
          >
            {endBusy ? 'Ending…' : 'End poll & show results'}
          </button>
        )}
      </div>

      {showEndConfirm &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="poll-feed-end-confirm-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby={`poll-feed-end-confirm-title-${poll.id}`}
            onClick={cancelEnd}
          >
            <div className="poll-feed-end-confirm-card" onClick={(e) => e.stopPropagation()}>
              <h3 id={`poll-feed-end-confirm-title-${poll.id}`}>End poll and publish results?</h3>
              <p>
                Everyone will immediately see vote totals for
                <strong> "{poll.title}"</strong>.
              </p>
              <div className="poll-feed-end-confirm-actions">
                <button
                  type="button"
                  className="poll-feed-end-confirm-btn-ghost poll-feed-end-confirm-cancel"
                  onClick={cancelEnd}
                  disabled={endBusy}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="poll-feed-end-confirm-btn-primary"
                  onClick={confirmEndPoll}
                  disabled={endBusy}
                >
                  {endBusy ? 'Ending…' : 'End poll'}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {showDeleteConfirm &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="poll-feed-delete-modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby={`poll-feed-delete-title-${poll.id}`}
            onClick={cancelDelete}
          >
            <div className="poll-feed-delete-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="poll-feed-delete-modal-header">
                <h3 id={`poll-feed-delete-title-${poll.id}`}>Delete Poll</h3>
                <button
                  type="button"
                  className="poll-feed-delete-modal-close"
                  onClick={cancelDelete}
                  disabled={deleteBusy}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="poll-feed-delete-modal-body">
                <p>{`Delete this poll: "${poll.title}"? All votes will be removed. This cannot be undone.`}</p>
              </div>
              <div className="poll-feed-delete-modal-footer">
                <button
                  type="button"
                  className="poll-feed-delete-cancel"
                  onClick={cancelDelete}
                  disabled={deleteBusy}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="poll-feed-delete-confirm"
                  onClick={confirmDelete}
                  disabled={deleteBusy}
                >
                  {deleteBusy ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default PollFeedCard;
